import { PropsWithChildren, useMemo } from 'react'
import { createContext } from 'lib/createContext'
import { VndlyTimesheetFilters, vndlyTimesheetFiltersSchema } from '../schema'
import { useCompanies } from 'queries/company'
import {
  useGenerateVndlyTimesheetMutation,
  useVndlyTimesheetIssuesQuery,
} from 'queries/vndly'
import { useVndlyTimesheetFilterQueryState } from '../hooks/useVndlyTimesheetFilterQueryState'

type VndlyTimesheetProviderState = {
  state: {
    filters: VndlyTimesheetFilters
    isCompaniesLoading: boolean
    companies: { value: number; label: string }[]
    downloadState: {
      canDownload: boolean
      isPending: boolean
      isError: boolean
    }
    issuesState: {
      requiresWorkWhileAdminIntervention: boolean
      workOrderAssociationIssues: NonNullable<
        ReturnType<typeof useVndlyTimesheetIssuesQuery>['data']
      >['workOrderAssociationIssues']
      queryState: {
        isEnabled: boolean
        isLoading: boolean
        isError: boolean
        isFetched: boolean
      }
    }
  }
  actions: {
    setFilters: (filters: VndlyTimesheetFilters) => void
    downloadTimesheet: ReturnType<
      typeof useGenerateVndlyTimesheetMutation
    >['mutate']
  }
}

export const [VndlyTimesheetContext, useVndlyTimesheetContext] =
  createContext<VndlyTimesheetProviderState>({
    name: 'VndlyTimesheetContext',
  })

export function VndlyTimesheetProvider({ children }: PropsWithChildren) {
  const { data: companies, isLoading: isCompaniesLoading } = useCompanies({
    excludeNonGroupCompanies: true,
  })

  const companiesOptions = useMemo(() => {
    return (
      companies
        ?.sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => ({
          value: c.id,
          label: c.name,
        })) ?? []
    )
  }, [companies])

  const [filters, setFilters] = useVndlyTimesheetFilterQueryState(
    companiesOptions.map((c) => c.value)
  )

  const isIssuesQueryEnabled =
    vndlyTimesheetFiltersSchema.safeParse(filters).success

  const {
    mutate: downloadTimesheet,
    isPending: isDownloadPending,
    isError: isDownloadError,
  } = useGenerateVndlyTimesheetMutation({
    companyIds: filters.companyIds,
    startDate: filters.dateFrom,
    endDate: filters.dateTo,
  })

  const {
    data: issuesData,
    isLoading: isIssuesLoading,
    isError: isIssuesError,
    isFetched: isIssuesFetched,
  } = useVndlyTimesheetIssuesQuery(
    {
      companyIds: filters.companyIds,
      startDate: filters.dateFrom,
      endDate: filters.dateTo,
    },
    isIssuesQueryEnabled
  )

  const canDownload = useMemo(() => {
    return (
      isIssuesQueryEnabled && !issuesData?.requiresWorkWhileAdminIntervention
    )
  }, [isIssuesQueryEnabled, issuesData])

  const value = useMemo(() => {
    return {
      state: {
        filters,
        isCompaniesLoading,
        companies: companiesOptions,
        downloadState: {
          canDownload,
          isPending: isDownloadPending,
          isError: isDownloadError,
        },
        issuesState: {
          requiresWorkWhileAdminIntervention:
            issuesData?.requiresWorkWhileAdminIntervention ?? false,
          workOrderAssociationIssues:
            issuesData?.workOrderAssociationIssues ?? [],
          queryState: {
            isEnabled: isIssuesQueryEnabled,
            isLoading: isIssuesLoading,
            isError: isIssuesError,
            isFetched: isIssuesFetched,
          },
        },
      },
      actions: {
        setFilters,
        downloadTimesheet,
      },
    } satisfies VndlyTimesheetProviderState
  }, [
    filters,
    companiesOptions,
    isCompaniesLoading,
    setFilters,
    downloadTimesheet,
    isDownloadPending,
    isDownloadError,
    canDownload,
    issuesData,
    isIssuesQueryEnabled,
    isIssuesLoading,
    isIssuesError,
    isIssuesFetched,
  ])

  return (
    <VndlyTimesheetContext.Provider value={value}>
      {children}
    </VndlyTimesheetContext.Provider>
  )
}
